"use client";
import React, { useState, useEffect, useRef } from 'react';
import { X, Percent, DollarSign, Calendar, Users, Package, Tag, IndianRupee } from 'lucide-react';
import { useDiscountType } from '../context/DiscountTypeContext';

const OfferModal = ({ isOpen, onClose, offer, products, categories, onSave }) => {
  const TITLE_WORD_LIMIT = 10;
const DESCRIPTION_WORD_LIMIT = 30;

  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    discount_type_id: '',
    value: '',
    minOrderValue: '',
    maxDiscount: '',
    usageLimit: '',
    usageCount: 0,
    userLimit: '',
    startDate: '',
    endDate: '',
    status: 'Active',
    applicableProducts: [],
    applicableCategories: [],
    excludeProducts: [],
    isFirstTimeUser: false,
    stackable: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef(null);

  const { discountTypes = [] } = useDiscountType();


const formatDateForInput = (date) => {
  if (!date) return "";

  // already in datetime-local format
  if (date.includes("T")) {
    return date.slice(0, 16);
  }

  // already in YYYY-MM-DD format
  if (date.includes("-") && date.split("-")[0].length === 4) {
    return `${date}T00:00`;
  }

  // DD-MM-YYYY format
  const [d, m, y] = date.split("-");
  return `${y}-${m}-${d}T00:00`;
};
useEffect(() => {
  if (offer) {
    setFormData({
      code: offer.code || "",
      title: offer.title || "",
      description: offer.description || "",
      discount_type_id: offer.discount_type_id || "",
      value: offer.value || "",
      minOrderValue: offer.minimum_order_value || "",
      maxDiscount: offer.max_discount || "",
      usageLimit: offer.total_usage_limit || "",
      userLimit: offer.per_user_limit || "",
      startDate: formatDateForInput(offer.start_date),
      endDate: formatDateForInput(offer.end_date),
      status: offer.status || "Inactive",
      applicableCategories: offer.category_id
        ? offer.category_id.toString().split(",").map(Number)
        : [],
      applicableProducts: offer.product_id
        ? offer.product_id.toString().split(",").map(Number)
        : [],
      isFirstTimeUser: offer.user_restrictions === "first_time_user",
      stackable: Boolean(offer.stackable),
    });
  } else {
    setFormData({
      code: '',
      title: '',
      description: '',
      discount_type_id: '',
      value: '',
      minOrderValue: '',
      maxDiscount: '',
      usageLimit: '',
      usageCount: 0,
      userLimit: '',
      startDate: '',
      endDate: '',
      status: 'Active',
      applicableProducts: [],
      applicableCategories: [],
      excludeProducts: [],
      isFirstTimeUser: false,
      stackable: false
    });
  }
}, [offer, isOpen]);


  const selectedDiscountType = discountTypes.find(
  d => String(d.id) === String(formData.discount_type_id)
);

const discountTypeName = selectedDiscountType?.name?.toLowerCase();


  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const generateOfferCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, code: result }));
  };

const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target;

  if (name === "title") {
  const words = value.trim().split(/\s+/);

  if (words.length > TITLE_WORD_LIMIT) {
    return;
  }
}

if (name === "description") {
  const words = value.trim().split(/\s+/);

  if (words.length > DESCRIPTION_WORD_LIMIT) {
    return;
  }
}
  if (name === "startDate" || name === "endDate") {
    const parts = value.split("-");
    const year = parts[0];

    if (year.length === 4 && value.length === 4) {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      const hours = "00";
      const minutes = "00";

      const corrected = `${year}-${month}-${day}T${hours}:${minutes}`;

      setFormData(prev => ({
        ...prev,
        [name]: corrected
      }));

      return;
    }

    if (year.length > 4) return;
  }

  setFormData(prev => ({
    ...prev,
    [name]: type === "checkbox"
      ? checked
      : type === "number"
        ? (value === "" ? "" : parseFloat(value))
        : value
  }));
};

  // ─── handleArrayChange ───────────────────────────────────────────────────────
  // When a CATEGORY is ticked:   auto-tick all active products in that category.
  // When a CATEGORY is unticked: remove its products unless another selected
  //                              category still covers them.
  // Product checkboxes work independently as before.
  const handleArrayChange = (name, value, checked) => {
    if (name === 'applicableCategories') {
      // Resolve whichever field name the product uses for its category reference
      const getCatId = (p) =>
        p.category_id ?? p.categoryId ?? p.category ?? p.sub_category_id ?? null;

      // DEBUG: remove after confirming it works
      console.log('[auto-select] toggled category id:', value);
      console.log('[auto-select] sample product fields:', products?.[0]);
      console.log('[auto-select] products in this category:',
        (products || []).filter(p => Number(getCatId(p)) === Number(value)).map(p => p.name)
      );

      // All active products belonging to the toggled category
      const categoryProductIds = (products || [])
        .filter(p => p.status === 'Active' && Number(getCatId(p)) === Number(value))
        .map(p => p.id);

      setFormData(prev => {
        const newCategories = checked
          ? [...prev.applicableCategories, value]
          : prev.applicableCategories.filter(id => id !== value);

        let newProducts = [...prev.applicableProducts];

        if (checked) {
          // Add products of the newly selected category (no duplicates)
          categoryProductIds.forEach(pid => {
            if (!newProducts.includes(pid)) newProducts.push(pid);
          });
        } else {
          // Products still covered by another selected category should stay
          const coveredByOtherCategories = new Set(
            (products || [])
              .filter(
                p =>
                  p.status === 'Active' &&
                  newCategories.map(Number).includes(Number(getCatId(p)))
              )
              .map(p => p.id)
          );

          newProducts = newProducts.filter(pid => {
            const isCategoryProduct = categoryProductIds.includes(pid);
            // Keep if not auto-added by this category, or still covered by another
            return !isCategoryProduct || coveredByOtherCategories.has(pid);
          });
        }

        return {
          ...prev,
          applicableCategories: newCategories,
          applicableProducts: newProducts,
        };
      });
    } else {
      // Default behaviour for applicableProducts and any other array fields
      setFormData(prev => ({
        ...prev,
        [name]: checked
          ? [...prev[name], value]
          : prev[name].filter(item => item !== value),
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Coupon code is required';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Coupon code must be at least 3 characters';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Coupon title is required';
    }

    if (getWordCount(formData.title) > TITLE_WORD_LIMIT) {
  newErrors.title = `Title cannot exceed ${TITLE_WORD_LIMIT} words`;
}

if (getWordCount(formData.description) > DESCRIPTION_WORD_LIMIT) {
  newErrors.description = `Description cannot exceed ${DESCRIPTION_WORD_LIMIT} words`;
}
    if (!formData.value || formData.value <= 0) {
      newErrors.value = 'Valid discount value is required';
    }

    if (discountTypeName === 'percentage' && formData.value > 100) {
  newErrors.value = 'Percentage discount cannot exceed 100%';
}

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (formData.usageLimit && formData.usageLimit <= 0) {
      newErrors.usageLimit = 'Usage limit must be greater than 0';
    }

    if (formData.userLimit && formData.userLimit <= 0) {
      newErrors.userLimit = 'User limit must be greater than 0';
    }

    if (formData.minOrderValue && formData.minOrderValue < 0) {
      newErrors.minOrderValue = 'Minimum order value cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateForAPI = (date) => {
  if (!date) return null;
  return date.split("T")[0];
};
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

const offerData = {
  id: offer?.id,

  code: formData.code.toUpperCase(),
  title: formData.title,
  description: formData.description,

  discount_type_id: Number(formData.discount_type_id),

  value: formData.value,
  maxDiscount: formData.maxDiscount || null,
  minOrderValue: formData.minOrderValue || null,

  usageLimit: formData.usageLimit || null,
  userLimit: formData.userLimit || null,

  startDate: formatDateForAPI(formData.startDate),
  endDate: formatDateForAPI(formData.endDate),

  status: formData.status,

  applicableCategories: formData.applicableCategories,
  applicableProducts: formData.applicableProducts,

  isFirstTimeUser: formData.isFirstTimeUser
};
      await onSave(offerData);
    } catch (error) {
      console.error('Error saving Coupon:', error);
      setErrors({ general: 'Failed to save Coupon. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;


const getWordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};
  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-gradient-to-br from-orange-50 to-orange-200 bg-opacity-95 shadow-2xl z-50 transform transition-all duration-300 ease-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ borderRadius: '10px 0 0 10px' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <button
          onClick={onClose}
          className="fixed left-4 top-4 w-8 h-8 bg-black text-white rounded-full hover:bg-gray-800 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white z-50 flex items-center justify-center shadow-lg"
          aria-label="Close Coupon modal"
          disabled={isLoading}
        >
          <X className="w-6 h-6" />
        </button>

        <div className="sticky top-0 bg-orange-50 p-8 z-10 border-b border-orange-200">
          <div className="text-center">
            <h2 id="modal-title" className="text-3xl font-bold text-teal-800 mb-2">
              {offer ? 'Edit Coupon' : 'Create New Coupon'}
            </h2>
            <div className="w-16 h-1 bg-orange-300 rounded-full mx-auto"></div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-8 space-y-8 min-h-[700px] rounded-[25px] m-4 bg-gray-400 bg-opacity-20"
        >
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label htmlFor="code" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Coupon Code *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="Enter Coupon code..."
                  className={`flex-1 px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 uppercase ${
                    errors.code ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-orange-200'
                  }`}
                  disabled={isLoading}
                  maxLength={20}
                />
                <button
                  type="button"
                  onClick={generateOfferCode}
                  className="px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400"
                  disabled={isLoading}
                >
                  <Tag className="w-5 h-5" />
                </button>
              </div>
              {errors.code && <p className="text-red-500 text-sm">{errors.code}</p>}
            </div>

            <div className="space-y-3">
              <label htmlFor="title" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Coupon Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter Coupon title..."
                className={`w-full px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 ${
                  errors.title ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-orange-200'
                }`}
                disabled={isLoading}
                maxLength={100}
              />
             <div className="flex justify-between text-xs mt-1">
  {errors.title && <p className="text-red-500">{errors.title}</p>}
 <span className="text-gray-500 ml-auto">
  {getWordCount(formData.title)} / {TITLE_WORD_LIMIT} words
</span>
</div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-3">
            <label htmlFor="description" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter Coupon description..."
              rows={3}
              className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 resize-none"
              disabled={isLoading}
              maxLength={200}
            />
            <div className="flex justify-end text-xs mt-1">
  <span className="text-gray-500">
    {getWordCount(formData.description)} / {DESCRIPTION_WORD_LIMIT} words
  </span>
</div>
          </div>

          {/* Discount Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <label htmlFor="type" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Discount Type *
              </label>
              <select
  id="discount_type_id"
  name="discount_type_id"
  value={formData.discount_type_id}
  onChange={handleInputChange}
  className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl"
>
  <option value="">Select Discount Type</option>

  {discountTypes
    .filter(d => d.status === "Active")
    .map((d) => (
      <option key={d.id} value={d.id}>
        {d.name}
      </option>
    ))}
</select>


            </div>

            <div className="space-y-3">
              <label htmlFor="value" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Discount Value *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {discountTypeName === 'percentage' ? (
  <Percent className="w-5 h-5 text-gray-400" />
) : (
  <IndianRupee className="w-5 h-5 text-gray-400" />
)}
                </div>
                <input
                  type="number"
                  id="value"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  placeholder={formData.type === 'percentage' ? '10' : '100'}
                  min="0"
                  max={formData.type === 'percentage' ? '100' : undefined}
                  step={formData.type === 'percentage' ? '0.1' : '0.01'}
                  className={`w-full pl-10 pr-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 ${
                    errors.value ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-orange-200'
                  }`}
                  disabled={isLoading || formData.type === 'free_shipping'}
                />
              </div>
              {errors.value && <p className="text-red-500 text-sm">{errors.value}</p>}
            </div>

            <div className="space-y-3">
              <label htmlFor="maxDiscount" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Max Discount (₹)
              </label>
              <input
                type="number"
                id="maxDiscount"
                name="maxDiscount"
                value={formData.maxDiscount}
                onChange={handleInputChange}
                placeholder="1000"
                min="0"
                step="1"
                className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500"
              />
            </div>
          </div>

          {/* Order Constraints */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label htmlFor="minOrderValue" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Minimum Order Value (₹)
              </label>
              <input
                type="number"
                id="minOrderValue"
                name="minOrderValue"
                value={formData.minOrderValue}
                onChange={handleInputChange}
                placeholder="500"
                min="0"
                step="0.01"
                className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500"
                disabled={isLoading}
              />
              {errors.minOrderValue && <p className="text-red-500 text-sm">{errors.minOrderValue}</p>}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                User Restrictions
              </label>
              <div className="space-y-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="isFirstTimeUser"
                    checked={formData.isFirstTimeUser}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    disabled={isLoading}
                  />
                  <span className="ml-3 text-sm text-gray-700">First-time users only</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="stackable"
                    checked={formData.stackable}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    disabled={isLoading}
                  />
                  <span className="ml-3 text-sm text-gray-700">Can be combined with other Coupons</span>
                </label>
              </div>
            </div>
          </div>

          {/* Usage Limits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label htmlFor="usageLimit" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Total Usage Limit
              </label>
              <input
                type="number"
                id="usageLimit"
                name="usageLimit"
                value={formData.usageLimit}
                onChange={handleInputChange}
                placeholder="100"
                min="1"
                className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500"
                disabled={isLoading}
              />
              {errors.usageLimit && <p className="text-red-500 text-sm">{errors.usageLimit}</p>}
              <p className="text-xs text-gray-600">Leave empty for unlimited usage</p>
            </div>

            <div className="space-y-3">
              <label htmlFor="userLimit" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Per User Limit
              </label>
              <input
                type="number"
                id="userLimit"
                name="userLimit"
                value={formData.userLimit}
                onChange={handleInputChange}
                placeholder="1"
                min="1"
                className="w-full px-4 py-3 bg-white bg-opacity-80 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500"
                disabled={isLoading}
              />
              {errors.userLimit && <p className="text-red-500 text-sm">{errors.userLimit}</p>}
              <p className="text-xs text-gray-600">Maximum uses per customer</p>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label htmlFor="startDate" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Start Date *
              </label>
              <input
                type="datetime-local"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 ${
                  errors.startDate ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-orange-200'
                }`}
                disabled={isLoading}
              />
              {errors.startDate && <p className="text-red-500 text-sm">{errors.startDate}</p>}
            </div>

            <div className="space-y-3">
              <label htmlFor="endDate" className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                End Date *
              </label>
              <input
                type="datetime-local"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-white bg-opacity-80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 ${
                  errors.endDate ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-orange-200'
                }`}
                disabled={isLoading}
              />
              {errors.endDate && <p className="text-red-500 text-sm">{errors.endDate}</p>}
            </div>
          </div>

          {/* Product/Category Restrictions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Applicable Categories
              </label>
              <div className="max-h-32 overflow-y-auto bg-white bg-opacity-80 border border-orange-200 rounded-xl p-3 space-y-2">
              {categories
  ?.filter(category => category.status === "Active")
  .map(category => (
                  <label key={category.id} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.applicableCategories.includes(category.id)}
                      onChange={(e) => handleArrayChange('applicableCategories', category.id, e.target.checked)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      disabled={isLoading}
                    />
                    <span className="ml-3 text-sm text-gray-700">{category.name}</span>
                  </label>
                ))}
                {(!categories || categories.length === 0) && (
                  <p className="text-sm text-gray-500">No categories available</p>
                )}
              </div>
              
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                Applicable Products
              </label>
              <div className="max-h-32 overflow-y-auto bg-white bg-opacity-80 border border-orange-200 rounded-xl p-3 space-y-2">
             {products
  ?.filter(product => product.status === "Active")
  .map(product => (
                  <label key={product.id} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.applicableProducts.includes(product.id)}
                      onChange={(e) => handleArrayChange('applicableProducts', product.id, e.target.checked)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      disabled={isLoading}
                    />
                    <span className="ml-3 text-sm text-gray-700">{product.name}</span>
                  </label>
                ))}
                {(!products || products.length === 0) && (
                  <p className="text-sm text-gray-500">No products available</p>
                )}
              </div>
            
            </div>
          </div>

          {/* Status */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Status
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value="Active"
                  checked={formData.status === 'Active'}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                  formData.status === 'Active'
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-300 bg-white group-hover:border-green-400'
                }`}>
                  {formData.status === 'Active' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">Active</span>
              </label>

              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value="Inactive"
                  checked={formData.status === 'Inactive'}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                  formData.status === 'Inactive'
                    ? 'border-gray-500 bg-gray-500'
                    : 'border-gray-300 bg-white group-hover:border-gray-400'
                }`}>
                  {formData.status === 'Inactive' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">Inactive</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-8 pb-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-white bg-opacity-80 text-gray-700 rounded-xl hover:bg-opacity-100 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-400 font-semibold border border-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-300 focus:outline-none  font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                offer ? 'Update Coupon' : 'Create Coupon'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default OfferModal;